// Service for handling subscription-related API calls
import axios from "./axios"
import { AxiosError } from "axios"

/**
 * Tracks a subscription status event
 * @param status - The status to track (subscription_page_open, subscription_completed)
 * @param email - The user's email
 * @param planId - The ID of the subscription plan
 * @param userApplicationId - The ID of the user application if available (deprecated)
 * @param code - The unique code of the user application
 * @returns Promise that resolves when the tracking is complete
 */
export const trackSubscriptionStatus = async (
  status: string,
  email?: string,
  planId?: string,
  userApplicationId?: number,
  code?: string,
): Promise<void> => {
  try {
    if (!email) {
      console.log("No email provided for subscription tracking, skipping")
      return
    }

    console.log(`Tracking subscription status: ${status} for email=${email}`)

    // Call the backend API to track the subscription status
    await axios.post("/integrations/subscription/track-status", {
      status,
      email,
      plan_id: planId,
      code: code,
    })

    console.log("Successfully tracked subscription status")
  } catch (error) {
    console.error("Error tracking subscription status:", error)
    // Don't throw the error to avoid disrupting the main flow
  }
}
/**
 * Creates a Stripe Checkout session for a subscription plan
 * @param planId - The ID of the subscription plan
 * @param seats - The number of seats for the subscription
 * @param addUsers - Whether additional users are being added
 * @param usersIncluded - The number of users included in the base plan
 * @param additionalUserCount - The number of additional users if applicable
 * @param paymentFrequency - The payment frequency (monthly or annual)
 * @param email - The user's email
 * @param code - The unique code of the user application
 * @returns Promise that resolves to the checkout URL
 */
export const createCheckoutSession = async (
  planId: string,
  usersIncluded: number,
  addUsers: boolean,
  additionalUserCount?: number,
  paymentFrequency: "monthly" | "annual" = "annual",
  email?: string,
  code?: string,
): Promise<string> => {
  try {
    // Calculate the number of seats based on whether additional users are selected
    const seats =
      addUsers && additionalUserCount
        ? usersIncluded + additionalUserCount
        : usersIncluded

    // Determine the environment based on the hostname
    const environment = getEnvironment()

    console.log(`Creating checkout session for plan_id=${planId}, seats=${seats}, environment=${environment}`)

    // Call the backend API to create a Stripe Checkout session
    const response = await axios.post(
      "/integrations/subscription/checkout-session",
      {
        plan_id: planId,
        seats: seats,
        environment: environment,
        payment_frequency: paymentFrequency,
        add_users: addUsers,
        email: email,
        code: code,
      },
    )

    const data = response.data

    // Return the checkout URL
    if (data.checkout_url) {
      console.log("Successfully created checkout session with URL")
      return data.checkout_url
    } else {
      console.error("API response did not contain checkout_url", data)
      throw new Error("No checkout URL returned from the server")
    }
  } catch (error: unknown) {
    // Check if error has properties typical of an AxiosError
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as AxiosError;
      console.error("Axios error creating checkout session:", {
        status: axiosError.response?.status,
        statusText: axiosError.response?.statusText,
        data: axiosError.response?.data
      })

      // Extract specific error message if available
      const errorMessage = axiosError.response?.data && 'errors' in axiosError.response.data
        ? axiosError.response.data.errors 
        : "Error connecting to the server. Please try again later."

      throw new Error(errorMessage)
    } else {
      console.error("Unknown error creating checkout session:", error)
      throw error
    }
  }
}

/**
 * Determines the environment based on REACT_APP_ENV
 * @returns 'prod' when REACT_APP_ENV is prod/producttion, otherwise 'test'
 */
export const getEnvironment = (): string => {
  const appEnv = (process.env.REACT_APP_ENV ?? "").toLowerCase()
  return appEnv === "production" ? "prod" : "test";
}
